import { cn } from "@/utils";
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerBodyProps,
  DrawerContent,
  DrawerContentProps,
  DrawerFooter,
  DrawerFooterProps,
  DrawerHeader,
  DrawerHeaderProps,
  DrawerProps,
} from "@heroui/react";
import { X } from "lucide-react";
import React from "react";

type LimeDrawerProps = {
  drawerProps?: Omit<DrawerProps, "children" | "title"> & {
    dataIdentifier?: string;
  };
  contentProps?: DrawerContentProps;
  headerProps?: DrawerHeaderProps;
  bodyProps?: DrawerBodyProps;
  footerProps?: DrawerFooterProps;

  /**
   * @description Try not to use the default header unless really necessary!
   */
  header?:
    | {
        useDefaultHeader?: false;
        closeButtonRowContent?: React.ReactNode;
        content?: React.ReactNode;
      }
    | {
        useDefaultHeader: true;
        content: React.ReactNode;
      };
  footer?: { content: React.ReactNode };
  body: { content: React.ReactNode };
  form?: React.ComponentPropsWithoutRef<"form">;
};

export const LimeDrawer = ({
  drawerProps,
  contentProps,
  headerProps,
  bodyProps,
  footerProps,

  header,
  footer,
  body,
  form,
}: LimeDrawerProps) => {
  return (
    <Drawer
      {...drawerProps}
      data-identifier={drawerProps?.dataIdentifier}
      hideCloseButton={!header?.useDefaultHeader}
    >
      <DrawerContent {...contentProps}>
        {(onClose) => {
          const content = (
            <Content
              headerProps={headerProps}
              bodyProps={bodyProps}
              footerProps={footerProps}
              header={header}
              body={body}
              footer={footer}
              onClose={onClose}
            />
          );

          return form != null ? (
            <form {...form} className={cn(form.className, "contents")}>
              {content}
            </form>
          ) : (
            content
          );
        }}
      </DrawerContent>
    </Drawer>
  );
};

const Content = ({
  headerProps,
  bodyProps,
  footerProps,
  header,
  body,
  footer,
  onClose,
}: Pick<
  LimeDrawerProps,
  "headerProps" | "bodyProps" | "footerProps" | "header" | "body" | "footer"
> & {
  onClose: () => void;
}) => {
  const headerContent = (onClose: () => void) => {
    if (header?.useDefaultHeader) {
      return header.content;
    }

    return (
      <div className="flex w-full flex-col">
        <div className="flex w-full items-center justify-between">
          {header?.closeButtonRowContent ?? <div />}

          <Button
            isIconOnly
            onPress={onClose}
            size="sm"
            variant="light"
            data-identifier="close-appointment-modal-button"
          >
            <X size={16} />
          </Button>
        </div>

        {header?.content}
      </div>
    );
  };

  return (
    <>
      <DrawerHeader {...headerProps}>{headerContent(onClose)}</DrawerHeader>
      <DrawerBody {...bodyProps}>{body.content}</DrawerBody>
      <DrawerFooter {...footerProps}>{footer?.content}</DrawerFooter>
    </>
  );
};
